/**
 * Akkrediteringsnivåer — «navn, hvilke soner de gir tilgang til» (PAGES.md, Settings [F2]).
 *
 * PER ÅRGANG. A festival's zones change when its site does, and last year's «Crew» badge must not
 * quietly open this year's backstage. `key` is a stable slug so a level can be matched across years
 * by a clone; the id cannot, because each year owns its own rows.
 *
 * ZONES ARE STRINGS THE ORGANISATION CHOOSES, never an enum — principle 9. A zone has no fields of
 * its own yet, so it is a name on a badge and nothing more. Scanning is [senere] in the plan, and
 * that is the day a zone needs an id of its own.
 *
 * NOT PAGED. A festival has a handful of badge levels, and a cursor here would be machinery for a
 * case that does not exist while costing every caller a paging loop to draw five rows — the same
 * argument attachments and task comments make.
 */
import { z } from 'zod';

const nameSchema = z.string().trim().min(1).max(80);

/** A zone is a label on a badge: short, and never empty once trimmed. */
const zoneSchema = z.string().trim().min(1).max(60);

/** Enough for a site with a stage per zone plus the shared areas; a badge listing forty is a bug. */
export const MAX_ZONES_PER_LEVEL = 30;

/**
 * Fargene et armbånd kan ha.
 *
 * ---------------------------------------------------------------------------------------------
 * TI NAVN, IKKE EN HEX. A wristband colour is not decoration: the gate reads red and lets you
 * through, reads blue and asks. So it is the organisation's own choice per year (principle 9) —
 * and a free picker would offer sixteen million of them, most unreadable against one background
 * or the other, with no answer at all in dark mode.
 *
 * Ten covers the bands a festival actually buys. Each name resolves to a token pair chosen for
 * contrast in BOTH modes, in `globals.css` — this list is the vocabulary, not the palette.
 */
export const ACCREDITATION_COLOURS = [
  'red',
  'orange',
  'amber',
  'green',
  'teal',
  'blue',
  'indigo',
  'purple',
  'pink',
  'slate',
] as const;
export const accreditationColourSchema = z.enum(ACCREDITATION_COLOURS);
export type AccreditationColour = z.infer<typeof accreditationColourSchema>;

/**
 * KORTET TIL UTSKRIFT (QA13 punkt 58, sak 253.81): tre utforminger festivalen velger mellom, og hva
 * som står på kortet. Nivåets farge er fargen over; kortet er 54 × 86 mm stående. Valgene sendes som
 * parametre til eksporten `festival.accreditation-cards` (`design`, `show`).
 *
 * `band`: hvitt kort med nivåets farge som et felt nederst. `solid`: hele kortet i fargen.
 * `outline`: hvitt med en ramme i fargen — det som skrives best ut på en svart-hvitt-skriver.
 */
export const ACCREDITATION_CARD_DESIGNS = ['band', 'solid', 'outline'] as const;
export type AccreditationCardDesign = (typeof ACCREDITATION_CARD_DESIGNS)[number];
export const ACCREDITATION_CARD_FIELDS = ['logo', 'zones', 'context', 'pass'] as const;
export type AccreditationCardField = (typeof ACCREDITATION_CARD_FIELDS)[number];

export const accreditationLevelDtoSchema = z
  .object({
    id: z.uuid(),
    editionId: z.uuid(),
    key: z.string(),
    name: z.string(),
    zones: z.array(z.string()),
    /** Null on a level nobody has coloured — it draws neutral, as every level did before. */
    colour: accreditationColourSchema.nullable(),
    sortOrder: z.number().int(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'AccreditationLevel' });

export const listAccreditationLevelsQuerySchema = z
  .object({ editionId: z.uuid() })
  .meta({ id: 'ListAccreditationLevelsQuery' });

export const accreditationLevelListResponseSchema = z
  .object({ items: z.array(accreditationLevelDtoSchema) })
  .meta({ id: 'AccreditationLevelListResponse' });

export const createAccreditationLevelRequestSchema = z
  .object({
    editionId: z.uuid(),
    name: nameSchema,
    zones: z.array(zoneSchema).max(MAX_ZONES_PER_LEVEL).default([]),
    colour: accreditationColourSchema.nullish(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .meta({ id: 'CreateAccreditationLevelRequest' });

/**
 * `key` is absent on purpose. It is generated from the first name and never moves, for the reason
 * a stage's does: it is what a clone matches on, and a slug that follows a rename stops matching
 * the level it was copied from.
 */
export const updateAccreditationLevelRequestSchema = z
  .object({
    name: nameSchema.optional(),
    zones: z.array(zoneSchema).max(MAX_ZONES_PER_LEVEL).optional(),
    colour: accreditationColourSchema.nullish(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateAccreditationLevelRequest' });

export type AccreditationLevelDto = z.infer<typeof accreditationLevelDtoSchema>;
export type ListAccreditationLevelsQuery = z.infer<typeof listAccreditationLevelsQuerySchema>;
export type AccreditationLevelListResponse = z.infer<typeof accreditationLevelListResponseSchema>;
export type CreateAccreditationLevelRequest = z.infer<typeof createAccreditationLevelRequestSchema>;
export type UpdateAccreditationLevelRequest = z.infer<typeof updateAccreditationLevelRequestSchema>;

// --------------------------------------------------------------- passtypene (#25a, QA13 runde 2)

/**
 * HVA PERSONEN FÅR UTLEVERT — den tredje aksen (migrering 0312).
 *
 * ------------------------------------------------------------------------------------------------
 * Atle: «Det mangler valg for hva personen skal få utlevert — festivalpass, dagspass osv.
 * Passtypene settes opp i Innstillinger sammen med akkrediteringsnivåene, velges per person og i
 * masseoperasjon, og dagspass knyttes til bestemte dager.»
 *
 * NIVÅET sier hvor de kommer inn, TYPEN sier hva de er her som, og PASSET sier hva som ligger på
 * disken. Ingen av de to første kunne svare på det tredje.
 *
 * Formen er nivåets, ord for ord der det går: `key` genereres av det første navnet og flytter seg
 * aldri, fordi det er det en klone matcher på.
 *
 * ------------------------------------------------------------------------------------------------
 * EN PASSTYPE ER BARE ET NAVN (QA14 #5, avgjørelse L-4, migrering 0333).
 *
 * `dayBound` sto her og sa at TYPEN krevde dager. Atle: «Fjern avkrysningen og logikken bak den.
 * Trengs det likevel dager, velges de når passet TILDELES en person.» Dagene har hele tiden stått
 * på personens egen rad — `accreditations.passDays`, en `date[]` — og det er den som svarer nå.
 */
export const accreditationPassDtoSchema = z
  .object({
    id: z.uuid(),
    editionId: z.uuid(),
    key: z.string(),
    name: z.string(),
    sortOrder: z.number().int(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'AccreditationPass' });

export const accreditationPassListResponseSchema = z
  .object({ items: z.array(accreditationPassDtoSchema) })
  .meta({ id: 'AccreditationPassListResponse' });

export const createAccreditationPassRequestSchema = z
  .object({
    editionId: z.uuid(),
    name: nameSchema,
    sortOrder: z.number().int().min(0).optional(),
  })
  .meta({ id: 'CreateAccreditationPassRequest' });

export const updateAccreditationPassRequestSchema = z
  .object({
    name: nameSchema.optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateAccreditationPassRequest' });

/** Så mange dager kan ett dagspass gjelde. En årgang med over seksti dager er ikke en årgang. */
export const MAX_PASS_DAYS = 60;

export type AccreditationPassDto = z.infer<typeof accreditationPassDtoSchema>;
export type AccreditationPassListResponse = z.infer<typeof accreditationPassListResponseSchema>;
export type CreateAccreditationPassRequest = z.infer<typeof createAccreditationPassRequestSchema>;
export type UpdateAccreditationPassRequest = z.infer<typeof updateAccreditationPassRequestSchema>;
